import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, Pill, Calendar, BedDouble, Lock, Store, BarChart3, UsersRound, LogOut, UserRound, Menu, X, ListOrdered, ShieldCheck, ReceiptText } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
    { to: "/patients", label: "Patients", icon: Users, testId: "nav-patients" },
    { to: "/medicines", label: "Medicines", icon: Pill, testId: "nav-medicines" },
    { to: "/appointments", label: "Appointments", icon: Calendar, testId: "nav-appointments" },
    { to: "/queue", label: "OPD Queue", icon: ListOrdered, testId: "nav-queue" },
    { to: "/ipd", label: "IPD", icon: BedDouble, testId: "nav-ipd" },
    { to: "/finance", label: "Finance", icon: Lock, testId: "nav-finance" },
    { to: "/pharmacy", label: "Medical Store", icon: Store, testId: "nav-pharmacy" },
    { to: "/reports", label: "NABH Reports", icon: BarChart3, testId: "nav-reports" },
    { to: "/compliance", label: "Compliance", icon: ShieldCheck, testId: "nav-compliance" },
    { to: "/superbills", label: "Superbills", icon: ReceiptText, testId: "nav-superbills" },
    { to: "/team", label: "Team", icon: UsersRound, testId: "nav-team" },
];

export default function DoctorLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const sidebar = (
        <div className="flex flex-col h-full">
            <Link to="/" className="flex items-center px-5 py-4 border-b border-slate-200" data-testid="brand-logo">
                <img src="/zen-logo.webp" alt="Zen Hospital" className="h-11 w-auto" />
            </Link>
            <nav className="flex-1 px-3 py-4 space-y-1">
                {NAV.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={n.testId}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 ${
                                isActive ? "bg-[#FFF4EC] text-[#E06D1F] border border-[#F6C9A4]" : "text-slate-600 hover:bg-slate-100 border border-transparent"
                            }`
                        }
                    >
                        <n.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                        {n.label}
                    </NavLink>
                ))}
            </nav>
            <div className="px-3 py-4 border-t border-slate-200 space-y-2">
                <div className="flex items-center gap-3 px-2" data-testid="doctor-profile-chip">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                        <UserRound className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{user?.name?.startsWith("Dr") ? user?.name : `Dr. ${user?.name}`}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.specialization}</p>
                    </div>
                </div>
                <Button data-testid="logout-btn" variant="outline" className="w-full justify-start gap-2 text-slate-600" onClick={handleLogout}>
                    <LogOut className="h-4 w-4" /> Sign out
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F8FAF9]">
            <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-slate-200 z-30">{sidebar}</aside>
            <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
                <Link to="/" data-testid="brand-logo-mobile">
                    <img src="/zen-logo.webp" alt="Zen Hospital" className="h-8 w-auto" />
                </Link>
                <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-slate-100">
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>
            {open && <div className="lg:hidden fixed inset-0 z-20 bg-black/30" onClick={() => setOpen(false)} data-testid="mobile-menu-overlay" />}
            <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-30 transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"}`}>{sidebar}</aside>
            <main className="lg:ml-64 pt-16 lg:pt-0">
                <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
